import axios from "@/plugins/axiosApi";
import { publicAccess } from "@/plugins/variables.js";
import router from '@/router/index';

function getToken() {
	const tk = localStorage.getItem("user-token");
	if (tk && tk !== "undefined") return JSON.parse(tk);
	if (publicAccess) router.push("/");
	else router.push("/login");
	return null;
}

function buildAuthHeaders(token = null, contentType = "application/json") {
	const headers = {
		Accept: "application/json",
		"Content-Type": contentType,
	};
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}
	return headers;
}

export default {
	namespaced: true,

	state: {
		token: null,
	},

	getters: {
		isAuthenticated: (state) => !!state.token,
		token: (state) => state.token,
	},

	mutations: {
		setToken: (state, payload) => {
			localStorage.setItem("user-token", JSON.stringify(payload));
			state.token = payload;
		},
		removeToken: (state) => {
			localStorage.removeItem("user-token");
			state.token = null;
		},
	},

	actions: {
		login: async ({ commit }, { model, data }) => {
			commit("removeToken");
			try {
				const response = await axios.post(`/${model}`, data, {
					headers: buildAuthHeaders(),
				});
				if (response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				commit("removeToken");
				return Promise.reject(error.response);
			}
		},
		setTokenFromLocalStorage: ({ commit }) => {
			const token = localStorage.getItem("user-token") || null;
			commit("setToken", JSON.parse(token));
		},
		logout: ({ commit }) => {
			commit("removeToken");
		},
		fetch: async ({ commit }, { model, updateToken = true }) => {
			const myToken = getToken();
			const url = model.toString().includes("?")
				? `/${model}&cb=${Date.now()}`
				: `/${model}?cb=${Date.now()}`;
			try {
				const response = await axios.get(url, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				if (error?.response?.status === 404 && error.response.data && !error.response.data.error) {
					return error.response.data;
				}
				return Promise.reject(error.response);
			}
		},
		raw: async ({ commit }, { model, updateToken = true }) => {
			const myToken = getToken();
			// const query = { "query": `SELECT COALESCE(json_agg(row_to_json(t)), '[]'::json) AS result FROM ${model} t;` };
			// const query = { "query": `SELECT json_agg(row_to_json(t)) AS result FROM ${model} t;` };
			const query = { "query": `SELECT json_agg(t) AS result FROM ${model} t;` };
			try {
				const response = await axios.post(`/raw/sql`, query, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		customRaw: async ({ commit }, { data, updateToken = true }) => {
			const myToken = getToken();
			const query = { "query": data };
			try {
				const response = await axios.post(`/raw/sql`, query, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		create: async ({ commit }, { model, data, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.post(`/${model}`, data, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		createForm: async ({ commit }, { model, data, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.post(`/${model}`, data, {
					headers: buildAuthHeaders(myToken, "multipart/form-data"),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		read: async ({ commit }, { model, id, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.get(`/${model}/${id}`, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		update: async ({ commit }, { model, id, data, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.put(`/${model}/${id}`, data, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		bulkUpdate: async ({ commit }, { model, id, data, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.put(`/${model}/${id}?do=bulk_update_rows`, data, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		updateForm: async ({ commit }, { model, id, data, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.put(`/${model}/${id}`, data, {
					headers: buildAuthHeaders(myToken, "multipart/form-data"),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		bulkUpdateForm: async ({ commit }, { model, id, data, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.put(`/${model}/${id}?do=bulk_update_signatures`, data, {
					headers: buildAuthHeaders(myToken, "multipart/form-data"),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		delete: async ({ commit }, { model, id, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.delete(`/${model}/${id}`, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		querySearch: async ({ commit }, { model, data, updateToken = true }) => {
			const myToken = getToken();
			data.params["cb"] = Date.now();
			try {
				const response = await axios.post(`/${model}/search`, null, {
					headers: buildAuthHeaders(myToken, "application/x-www-form-urlencoded"),
					params: data.params,
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				if (error?.response?.status === 404) {
					return error.response.data;
				}
				return Promise.reject(error.response);
			}
		},
		jsonSearch: async ({ commit }, { model, data, updateToken = true }) => {
			const myToken = getToken();
			data['cb'] = Date.now();
			try {
				const response = await axios.post(`/${model}/search`, data, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				if (error?.response?.status === 404) {
					return error.response.data;
				}
				return Promise.reject(error.response);
			}
		},
		customAction: async ({ commit }, { model, action, data, updateToken = true }) => {
			const myToken = getToken();
			try {
				const response = await axios.post(`/${model}/custom_action/${action}`, data, {
					headers: buildAuthHeaders(myToken),
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		count: async ({ commit }, { model, data, updateToken = true }) => {
			const myToken = getToken();
			data.params["count"] = "true";
			try {
				const response = await axios.get(`/${model}`, {
					headers: buildAuthHeaders(myToken),
					params: data.params,
				});
				if (updateToken && response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
		getInfo: async ({ commit }, { model }) => {
			const myToken = getToken();
			try {
				const response = await axios.get(`/info/${model}`, {
					headers: buildAuthHeaders(myToken),
				});
				if (response.headers.token) commit("setToken", response.headers.token);
				return response.data;
			} catch (error) {
				return Promise.reject(error.response);
			}
		},
	},
};
